<template>
  <el-dialog
    :title="$tc('general.print')"
    :visible.sync="visible"
    width="700px"
    @close="handleClose"
  >
    <div class="print-options">
      <!-- 1. Club (Sports Association) - Contextual Print -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.club.checked"
          @change="handleClubToggle($event)"
        >
          {{ $tc('general.club', 1) }}
        </el-checkbox>
        <div v-if="selectedOptions.club.checked" class="print-option-children">
          <el-select
            v-model="selectedOptions.club.selectedClubIds"
            :placeholder="$tc('general.club', 1)"
            multiple
            collapse-tags
            clearable
            style="width: 100%; margin-bottom: 15px;"
            @change="handleClubChange"
          >
            <el-option
              v-for="club in registeredClubs"
              :key="club.id"
              :label="club.shortName || club.name"
              :value="club.id"
            />
          </el-select>
          <div v-if="selectedOptions.club.selectedClubIds && selectedOptions.club.selectedClubIds.length > 0" class="club-options">
            <el-checkbox
              :value="selectedOptions.club.clubInformation"
              @change="selectedOptions.club.clubInformation = $event"
            >
              Informacije o klubu
            </el-checkbox>
            <el-checkbox
              :value="selectedOptions.club.kotizacija"
              @change="selectedOptions.club.kotizacija = $event"
            >
              {{ $tc('general.registration_fee') }}
            </el-checkbox>
            <el-checkbox
              :value="selectedOptions.club.competitors"
              @change="selectedOptions.club.competitors = $event"
            >
              {{ $tc('tournament.competitors', 2) }}
            </el-checkbox>
            <el-checkbox
              :value="selectedOptions.club.teams"
              @change="selectedOptions.club.teams = $event"
            >
              {{ $tc('tournament.teams') }}
            </el-checkbox>
            <el-checkbox
              :value="selectedOptions.club.judges"
              @change="selectedOptions.club.judges = $event"
            >
              {{ $tc('tournament.judges') }}
            </el-checkbox>
            <el-checkbox
              :value="selectedOptions.club.officials"
              @change="selectedOptions.club.officials = $event"
            >
              {{ $tc('club.official', 2) }}
            </el-checkbox>
            <el-checkbox
              :value="selectedOptions.club.coaches"
              @change="handleCoachesToggle($event)"
            >
              {{ $tc('club.coach', 2) }}
            </el-checkbox>
            <div v-if="selectedOptions.club.coaches" class="coach-certificate-options">
              <el-checkbox
                :value="selectedOptions.club.coachCertificates.certificate"
                @change="selectedOptions.club.coachCertificates.certificate = $event"
              >
                {{ $tc('tournament.certificate') }}
              </el-checkbox>
              <el-checkbox
                :value="selectedOptions.club.coachCertificates.diploma"
                @change="selectedOptions.club.coachCertificates.diploma = $event"
              >
                {{ $tc('tournament.diploma') }}
              </el-checkbox>
              <el-checkbox
                :value="selectedOptions.club.coachCertificates.thankYouLetter"
                @change="selectedOptions.club.coachCertificates.thankYouLetter = $event"
              >
                {{ $tc('tournament.thank_you_letter') }}
              </el-checkbox>
            </div>
          </div>
          <div v-else class="club-placeholder">
            <span class="text-muted">Izaberite sportsko udruženje</span>
          </div>
        </div>
      </div>

      <!-- 2. Competitors (Global) -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.globalCompetitors"
          @change="selectedOptions.globalCompetitors = $event"
        >
          {{ $tc('tournament.competitors', 2) }}
        </el-checkbox>
      </div>

      <!-- 3. Teams (Global) -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.globalTeams"
          @change="selectedOptions.globalTeams = $event"
        >
          {{ $tc('tournament.teams') }}
        </el-checkbox>
      </div>

      <!-- 4. Judges (Global) -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.globalJudges"
          @change="selectedOptions.globalJudges = $event"
        >
          {{ $tc('tournament.judges') }}
        </el-checkbox>
      </div>

      <!-- 5. Officials (Global) -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.globalOfficials"
          @change="selectedOptions.globalOfficials = $event"
        >
          {{ $tc('club.official', 2) }}
        </el-checkbox>
      </div>

      <!-- 6. Coaches (Global) -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.globalCoaches"
          @change="selectedOptions.globalCoaches = $event"
        >
          {{ $tc('club.coach', 2) }}
        </el-checkbox>
      </div>

      <!-- 7. Basic Information -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.basicInformation"
          @change="selectedOptions.basicInformation = $event"
        >
          {{ $tc('tournament.basic_information') }}
        </el-checkbox>
      </div>

      <!-- 8. Individual Disciplines -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.individualDisciplines.checked"
          @change="handleIndividualDisciplinesToggle($event)"
        >
          {{ $tc('tournament.individual_disciplines') }}
        </el-checkbox>
        <div v-if="selectedOptions.individualDisciplines.checked" class="print-option-children">
          <div class="print-option-nested">
            <el-select
              v-model="selectedOptions.individualDisciplines.selectedCategory"
              :placeholder="$tc('tournament.select_category')"
              clearable
              style="width: 100%; margin-bottom: 10px;"
              @change="handleCategoryChange('individual')"
            >
              <el-option
                v-for="category in individualCategories"
                :key="category.id"
                :label="category.name"
                :value="String(category.id)"
              />
            </el-select>
            <el-select
              v-model="selectedOptions.individualDisciplines.selectedDiscipline"
              :placeholder="$tc('tournament.select_discipline')"
              :disabled="!selectedOptions.individualDisciplines.selectedCategory"
              clearable
              style="width: 100%; margin-bottom: 10px;"
              @change="handleDisciplineChange('individual')"
            >
              <el-option
                v-for="discipline in individualDisciplines"
                :key="discipline.id"
                :label="discipline.name"
                :value="String(discipline.id)"
              />
            </el-select>
            <div v-if="selectedOptions.individualDisciplines.selectedDiscipline" class="gender-options">
              <el-checkbox
                :value="selectedOptions.individualDisciplines.men"
                @change="selectedOptions.individualDisciplines.men = $event"
              >
                {{ $tc('general.men') }}
              </el-checkbox>
              <el-checkbox
                :value="selectedOptions.individualDisciplines.women"
                @change="selectedOptions.individualDisciplines.women = $event"
              >
                {{ $tc('general.women') }}
              </el-checkbox>
            </div>
          </div>
        </div>
      </div>

      <!-- 9. Team Disciplines -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.teamDisciplines.checked"
          @change="handleTeamDisciplinesToggle($event)"
        >
          {{ $tc('tournament.team_disciplines') }}
        </el-checkbox>
        <div v-if="selectedOptions.teamDisciplines.checked" class="print-option-children">
          <div class="print-option-nested">
            <el-select
              v-model="selectedOptions.teamDisciplines.selectedCategory"
              :placeholder="$tc('tournament.select_category')"
              clearable
              style="width: 100%; margin-bottom: 10px;"
              @change="handleCategoryChange('team')"
            >
              <el-option
                v-for="category in teamCategories"
                :key="category.id"
                :label="category.name"
                :value="String(category.id)"
              />
            </el-select>
            <el-select
              v-model="selectedOptions.teamDisciplines.selectedDiscipline"
              :placeholder="$tc('tournament.select_discipline')"
              :disabled="!selectedOptions.teamDisciplines.selectedCategory"
              clearable
              style="width: 100%; margin-bottom: 10px;"
              @change="handleDisciplineChange('team')"
            >
              <el-option
                v-for="discipline in teamDisciplines"
                :key="discipline.id"
                :label="discipline.name"
                :value="String(discipline.id)"
              />
            </el-select>
            <div v-if="selectedOptions.teamDisciplines.selectedDiscipline" class="gender-options">
              <el-checkbox
                :value="selectedOptions.teamDisciplines.men"
                @change="selectedOptions.teamDisciplines.men = $event"
              >
                {{ $tc('general.men') }}
              </el-checkbox>
              <el-checkbox
                :value="selectedOptions.teamDisciplines.women"
                @change="selectedOptions.teamDisciplines.women = $event"
              >
                {{ $tc('general.women') }}
              </el-checkbox>
            </div>
          </div>
        </div>
      </div>

      <!-- 10. Registration Fee -->
      <div class="print-option-group">
        <el-checkbox
          :value="selectedOptions.registrationFee"
          @change="selectedOptions.registrationFee = $event"
        >
          {{ $tc('general.registration_fee') }}
        </el-checkbox>
      </div>
    </div>

    <template v-slot:footer>
      <el-button @click="handleClose">{{ $tc('general.cancel') }}</el-button>
      <el-button type="button" @click="handlePrint" :disabled="!hasSelectedOptions">
        {{ $tc('general.print') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
export default {
  name: 'TournamentPrintOptionsModal',

  props: {
    value: {
      type: Boolean,
      default: false
    },
    tournament: {
      type: Object,
      required: true
    },
    competitors: {
      type: Array,
      default: () => []
    },
    teams: {
      type: Array,
      default: () => []
    },
    clubs: {
      type: Array,
      default: () => []
    },
    coaches: {
      type: Array,
      default: () => []
    },
    referees: {
      type: Array,
      default: () => []
    },
    officials: {
      type: Array,
      default: () => []
    },
    infoData: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      selectedOptions: {
        club: {
          checked: false,
          selectedClubIds: [],
          clubInformation: false,
          kotizacija: false,
          competitors: false,
          teams: false,
          judges: false,
          officials: false,
          coaches: false,
          coachCertificates: {
            certificate: false,
            diploma: false,
            thankYouLetter: false
          }
        },
        globalCompetitors: false,
        globalTeams: false,
        globalJudges: false,
        globalOfficials: false,
        globalCoaches: false,
        basicInformation: false,
        individualDisciplines: {
          checked: false,
          selectedCategory: null,
          selectedDiscipline: null,
          men: false,
          women: false
        },
        teamDisciplines: {
          checked: false,
          selectedCategory: null,
          selectedDiscipline: null,
          men: false,
          women: false
        },
        registrationFee: false
      }
    }
  },

  computed: {
    visible: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },

    hasSelectedOptions () {
      // Check if clubs are selected (with default full print, just having clubs selected is enough)
      const hasClubsSelected = this.selectedOptions.club.checked &&
        this.selectedOptions.club.selectedClubIds &&
        this.selectedOptions.club.selectedClubIds.length > 0

      // Check club-specific explicit options (for backward compatibility and explicit selection)
      const hasExplicitClubOptions = hasClubsSelected && (
        this.selectedOptions.club.clubInformation ||
        this.selectedOptions.club.kotizacija ||
        this.selectedOptions.club.competitors ||
        this.selectedOptions.club.teams ||
        this.selectedOptions.club.judges ||
        this.selectedOptions.club.officials ||
        this.selectedOptions.club.coaches ||
        this.selectedOptions.club.coachCertificates.certificate ||
        this.selectedOptions.club.coachCertificates.diploma ||
        this.selectedOptions.club.coachCertificates.thankYouLetter
      )

      // Check global options
      const hasGlobalOptions = (
        this.selectedOptions.globalCompetitors ||
        this.selectedOptions.globalTeams ||
        this.selectedOptions.globalJudges ||
        this.selectedOptions.globalOfficials ||
        this.selectedOptions.globalCoaches
      )

      // Check other options
      const hasOtherOptions = (
        this.selectedOptions.basicInformation ||
        this.selectedOptions.registrationFee
      )

      // Check discipline options
      // Allow printing if category is selected (will print all disciplines and genders under that category)
      // Discipline and gender filters are optional - if not selected, all will be included
      const hasIndividualDisciplines = this.selectedOptions.individualDisciplines.checked &&
        this.selectedOptions.individualDisciplines.selectedCategory

      const hasTeamDisciplines = this.selectedOptions.teamDisciplines.checked &&
        this.selectedOptions.teamDisciplines.selectedCategory

      // Allow printing if clubs are selected (default full print) OR if explicit options are selected
      return hasClubsSelected || hasExplicitClubOptions || hasGlobalOptions || hasOtherOptions || hasIndividualDisciplines || hasTeamDisciplines
    },

    registeredClubs () {
      return this.tournament?.clubs || []
    },

    allCategories () {
      return this.$store.state.categories.data || []
    },

    individualCategories () {
      if (!this.allCategories || !this.allCategories.length) return []
      if (!this.tournament?.disciplines || !this.tournament.disciplines.length) return []

      // Extract categories directly from tournament disciplines
      // Each discipline has disciplineCategories which contains categoryId
      const categoryIdsSet = new Set()

      this.tournament.disciplines.forEach(tournamentDiscipline => {
        const discipline = tournamentDiscipline.discipline
        if (discipline?.disciplineCategories && Array.isArray(discipline.disciplineCategories)) {
          discipline.disciplineCategories.forEach(dc => {
            const catId = String(dc.categoryId || dc.category?.id || '')
            if (catId) {
              categoryIdsSet.add(catId)
            }
          })
        }
      })

      // Return categories that are assigned to this tournament's disciplines
      return this.allCategories.filter(c =>
        !c.isArchived && categoryIdsSet.has(String(c.id))
      )
    },

    teamCategories () {
      if (!this.allCategories || !this.allCategories.length) return []
      if (!this.tournament?.teamDisciplines || !this.tournament.teamDisciplines.length) return []

      // Extract categories directly from tournament team disciplines
      // Each discipline has disciplineCategories which contains categoryId
      const categoryIdsSet = new Set()

      this.tournament.teamDisciplines.forEach(tournamentDiscipline => {
        const discipline = tournamentDiscipline.discipline
        if (discipline?.disciplineCategories && Array.isArray(discipline.disciplineCategories)) {
          discipline.disciplineCategories.forEach(dc => {
            const catId = String(dc.categoryId || dc.category?.id || '')
            if (catId) {
              categoryIdsSet.add(catId)
            }
          })
        }
      })

      // Return categories that are assigned to this tournament's team disciplines
      return this.allCategories.filter(c =>
        !c.isArchived && categoryIdsSet.has(String(c.id))
      )
    },

    individualDisciplines () {
      const selectedCategoryId = this.selectedOptions.individualDisciplines.selectedCategory
      if (!selectedCategoryId) return []
      if (!this.tournament?.disciplines || !this.tournament.disciplines.length) return []

      // Find disciplines from tournament that match the selected category
      const disciplinesMap = new Map()

      this.tournament.disciplines.forEach(tournamentDiscipline => {
        const discipline = tournamentDiscipline.discipline
        if (discipline?.disciplineCategories && Array.isArray(discipline.disciplineCategories)) {
          const hasCategory = discipline.disciplineCategories.some(
            dc => String(dc.categoryId || dc.category?.id) === String(selectedCategoryId)
          )
          if (hasCategory && discipline.id) {
            const discId = String(discipline.id)
            if (!disciplinesMap.has(discId)) {
              disciplinesMap.set(discId, {
                id: discipline.id,
                name: discipline.name || ''
              })
            }
          }
        }
      })

      return Array.from(disciplinesMap.values())
    },

    teamDisciplines () {
      const selectedCategoryId = this.selectedOptions.teamDisciplines.selectedCategory
      if (!selectedCategoryId) return []
      if (!this.tournament?.teamDisciplines || !this.tournament.teamDisciplines.length) return []

      // Find disciplines from tournament team disciplines that match the selected category
      const disciplinesMap = new Map()

      this.tournament.teamDisciplines.forEach(tournamentDiscipline => {
        const discipline = tournamentDiscipline.discipline
        if (discipline?.disciplineCategories && Array.isArray(discipline.disciplineCategories)) {
          const hasCategory = discipline.disciplineCategories.some(
            dc => String(dc.categoryId || dc.category?.id) === String(selectedCategoryId)
          )
          if (hasCategory && discipline.id) {
            const discId = String(discipline.id)
            if (!disciplinesMap.has(discId)) {
              disciplinesMap.set(discId, {
                id: discipline.id,
                name: discipline.name || ''
              })
            }
          }
        }
      })

      return Array.from(disciplinesMap.values())
    }
  },

  methods: {
    handleClubToggle (checked) {
      this.selectedOptions.club.checked = checked
      if (!checked) {
        // Clear all club-specific options when unchecked
        this.selectedOptions.club.selectedClubIds = []
        this.selectedOptions.club.clubInformation = false
        this.selectedOptions.club.kotizacija = false
        this.selectedOptions.club.competitors = false
        this.selectedOptions.club.teams = false
        this.selectedOptions.club.judges = false
        this.selectedOptions.club.officials = false
        this.selectedOptions.club.coaches = false
        this.selectedOptions.club.coachCertificates = {
          certificate: false,
          diploma: false,
          thankYouLetter: false
        }
      }
    },

    handleClubChange () {
      // Reset club-specific options when all clubs are cleared
      if (!this.selectedOptions.club.selectedClubIds || this.selectedOptions.club.selectedClubIds.length === 0) {
        this.selectedOptions.club.clubInformation = false
        this.selectedOptions.club.kotizacija = false
        this.selectedOptions.club.competitors = false
        this.selectedOptions.club.teams = false
        this.selectedOptions.club.judges = false
        this.selectedOptions.club.officials = false
        this.selectedOptions.club.coaches = false
        this.selectedOptions.club.coachCertificates = {
          certificate: false,
          diploma: false,
          thankYouLetter: false
        }
      }
    },

    handleCoachesToggle (checked) {
      this.selectedOptions.club.coaches = checked
      if (!checked) {
        this.selectedOptions.club.coachCertificates = {
          certificate: false,
          diploma: false,
          thankYouLetter: false
        }
      }
    },

    handleIndividualDisciplinesToggle (checked) {
      this.selectedOptions.individualDisciplines.checked = checked
      if (!checked) {
        this.selectedOptions.individualDisciplines.selectedCategory = ''
        this.selectedOptions.individualDisciplines.selectedDiscipline = ''
        this.selectedOptions.individualDisciplines.men = false
        this.selectedOptions.individualDisciplines.women = false
      }
    },

    handleTeamDisciplinesToggle (checked) {
      this.selectedOptions.teamDisciplines.checked = checked
      if (!checked) {
        this.selectedOptions.teamDisciplines.selectedCategory = ''
        this.selectedOptions.teamDisciplines.selectedDiscipline = ''
        this.selectedOptions.teamDisciplines.men = false
        this.selectedOptions.teamDisciplines.women = false
      }
    },

    handleCategoryChange (type) {
      if (type === 'individual') {
        // Reset discipline and gender when category changes
        this.selectedOptions.individualDisciplines.selectedDiscipline = ''
        this.selectedOptions.individualDisciplines.men = false
        this.selectedOptions.individualDisciplines.women = false
      } else {
        // Reset discipline and gender when category changes
        this.selectedOptions.teamDisciplines.selectedDiscipline = ''
        this.selectedOptions.teamDisciplines.men = false
        this.selectedOptions.teamDisciplines.women = false
      }
    },

    handleDisciplineChange (type) {
      // Gender options remain when discipline changes, only reset if discipline is cleared
      if (type === 'individual' && !this.selectedOptions.individualDisciplines.selectedDiscipline) {
        this.selectedOptions.individualDisciplines.men = false
        this.selectedOptions.individualDisciplines.women = false
      } else if (type === 'team' && !this.selectedOptions.teamDisciplines.selectedDiscipline) {
        this.selectedOptions.teamDisciplines.men = false
        this.selectedOptions.teamDisciplines.women = false
      }
    },

    handleClose () {
      this.visible = false
      this.resetOptions()
    },

    resetOptions () {
      this.selectedOptions = {
        club: {
          checked: false,
          selectedClubIds: [],
          clubInformation: false,
          kotizacija: false,
          competitors: false,
          teams: false,
          judges: false,
          officials: false,
          coaches: false,
          coachCertificates: {
            certificate: false,
            diploma: false,
            thankYouLetter: false
          }
        },
        globalCompetitors: false,
        globalTeams: false,
        globalJudges: false,
        globalOfficials: false,
        globalCoaches: false,
        basicInformation: false,
        individualDisciplines: {
          checked: false,
          selectedCategory: '',
          selectedDiscipline: '',
          men: false,
          women: false
        },
        teamDisciplines: {
          checked: false,
          selectedCategory: '',
          selectedDiscipline: '',
          men: false,
          women: false
        },
        registrationFee: false
      }
    },

    handlePrint (event) {
      // Prevent any default form submission or event propagation
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      // Prepare options with default full print logic
      const optionsToPrint = { ...this.selectedOptions }

      // Check if club is selected
      if (optionsToPrint.club.checked &&
          optionsToPrint.club.selectedClubIds &&
          optionsToPrint.club.selectedClubIds.length > 0) {
        // Check if any club-specific print options are explicitly selected
        const hasExplicitClubOptions =
          optionsToPrint.club.clubInformation ||
          optionsToPrint.club.kotizacija ||
          optionsToPrint.club.competitors ||
          optionsToPrint.club.teams ||
          optionsToPrint.club.judges ||
          optionsToPrint.club.officials ||
          optionsToPrint.club.coaches ||
          optionsToPrint.club.coachCertificates.certificate ||
          optionsToPrint.club.coachCertificates.diploma ||
          optionsToPrint.club.coachCertificates.thankYouLetter

        // If no explicit options selected, enable all club sections (default full print)
        if (!hasExplicitClubOptions) {
          optionsToPrint.club.clubInformation = true
          optionsToPrint.club.competitors = true
          optionsToPrint.club.teams = true
          optionsToPrint.club.judges = true
          optionsToPrint.club.officials = true
          optionsToPrint.club.coaches = true
        }
      }

      // If only club is selected and no other options, the default full print for club is already set above
      // If other options are selected, they will be printed as explicitly selected

      // Emit print event - parent will handle closing modal after print is triggered
      this.$emit('print', optionsToPrint)
      // Don't close immediately - let parent handle it after print dialog opens
    }
  }
}
</script>

<style lang="scss" scoped>
.print-options {
  .print-option-group {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e4e7ed;

    &:last-child {
      border-bottom: none;
    }

    .print-option-header {
      margin-bottom: 12px;
      font-size: 14px;
    }

    .print-option-children {
      margin-left: 24px;
      margin-top: 10px;

      .club-options {
        .el-checkbox {
          display: block;
          margin-bottom: 8px;
        }

        .coach-certificate-options {
          margin-left: 24px;
          margin-top: -4px;
          margin-bottom: 8px;
        }
      }

      .club-placeholder {
        padding: 8px 0;
        font-style: italic;
        color: #909399;
      }

      .print-option-nested {
        margin-top: 10px;

        .gender-options {
          margin-top: 10px;

          .el-checkbox {
            margin-right: 20px;
          }
        }
      }
    }
  }
}

.text-muted {
  color: #909399;
}
</style>
